import 'pages/login/index.css'
import 'pages/settings/index.css'
import React from 'react'
import WeakPasswordError from 'components/password/weak-password-error'
import { useTranslation } from '@disco/i18n/use-translation'
import { Trans } from '@disco/i18n/trans'
const PASSWORD_STRENGTH = 'standard'
type Props = {
  onBackToLogin: () => void
  error: any
}

/**
 * ExplainError component for displaying signup error messages
 *
 * Provides user-friendly error messages for various signup failures including
 * weak passwords, breached passwords, existing email accounts, and other validation errors.
 * Handles different error formats and provides actionable guidance.
 *
 * @param props - Component props
 * @param props.onBackToLogin - Callback function to navigate back to login form
 * @param props.error - Error object or string containing error details
 * @returns JSX element containing the appropriate error message, or null if no error
 */
const ExplainError = ({ onBackToLogin, error }: Props): React.ReactNode => {
  const { t } = useTranslation('accounts')
  if (!error) return null

  if (error === 'WEAK_PASSWORD') {
    return <WeakPasswordError passwordStrength={PASSWORD_STRENGTH} />
  }

  if (error === 'breached_password') {
    return (
      // TODO: duplicates ChangePassword errors - extract into new component
      <>
        {t(
          'accounts:sorryThatPasswordIsNotStrongEnoughAndMayHaveBeenUsedOnOtherSystemsPleaseCreateANewMoreSecurePassword',
          'Sorry, that password is not strong enough, and may have been used on other systems. Please create a new, more secure password.',
        )}{' '}
        <a
          rel="noopener noreferrer"
          target="_blank"
          href="https://support.disco.ac/home/q-how-do-i-choose-a-secure-password"
          className="underline text-neutral-200"
        >
          {t('accounts:findOutMore', 'Find out more.')}
        </a>
      </>
    )
  }

  if (error.res?.reason || error.reason) {
    let reason = error.res?.reason || error.reason

    switch (reason) {
      case 'EMAIL_EXISTS': {
        if (error.has_password) {
          return (
            <div>
              {t(
                'accounts:itLooksLikeTheresAlreadyADiscoCatalogAccountForThatEmail',
                'It looks like there’s already a DISCO Catalog account for that email.',
              )}
            </div>
          )
        } else {
          return (
            <div>
              <Trans
                t={t}
                i18nKey="accounts:itLooksLikeTheresAlreadyADiscoAccountForThatEmailSignInHere"
              >
                It looks like there’s already a DISCO account for that email.
                Sign in{' '}
                <span
                  className="cursor-pointer underline"
                  onClick={onBackToLogin}
                >
                  here
                </span>
                .
              </Trans>
            </div>
          )
        }
      }

      case 'invite_already_accepted':
        return t('accounts:inviteAlreadyAccepted', 'Invite already accepted')

      default:
        return reason
    }
  } else if (typeof error !== 'string') {
    try {
      let v: any = Object.values(error)
      if (v[0]) v = v[0][0]
      return v
    } catch (err) {}
  }

  return error
}

export default ExplainError
